import { Directive, ElementRef, Input, OnInit, OnDestroy } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { Subscription } from 'rxjs';

/**
 * Directive that applies US phone number formatting: (XXX) XXX-XXXX
 * Automatically formats input as user types and maintains cursor position.
 *
 * @example
 * <input type="tel"
 *        phoneMask
 *        [phoneControl]="form.controls['phone']"
 *        [preValue]="form.value.phone"
 *        [elementId]="'tel'" />
 */
@Directive({
  standalone: true,
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[phoneMask]'
})
export class PhoneMaskDirective implements OnInit, OnDestroy {
  private _phoneControl: AbstractControl | null = null;
  private _previousValue = '';
  private _elementId = '';
  private subscription: Subscription | null = null;

  @Input()
  set phoneControl(control: AbstractControl | null) {
    this._phoneControl = control;
  }

  @Input()
  set preValue(value: string) {
    this._previousValue = value || '';
  }

  @Input()
  set elementId(id: string) {
    this._elementId = id || '';
  }

  // eslint-disable-next-line @angular-eslint/prefer-inject
  constructor(private el: ElementRef) {}

  ngOnInit(): void {
    if (!this._phoneControl) {
      console.warn('PhoneMaskDirective: phoneControl is required');
      return;
    }
    if (!this._elementId) {
      console.warn('PhoneMaskDirective: elementId is required');
      return;
    }
    this.initializePhoneMask();
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  /**
   * Initialize phone masking by subscribing to form control value changes
   */
  private initializePhoneMask(): void {
    if (!this._phoneControl) {
      return;
    }

    this.subscription = this._phoneControl.valueChanges.subscribe((currentValue) => {
      this.handleValueChange(currentValue);
    });
  }

  /**
   * Handle form control value changes and apply phone formatting
   */
  private handleValueChange(currentValue: string): void {
    if (!this._phoneControl) {
      return;
    }

    const inputElement = this.getInputElement();
    if (!inputElement) {
      return;
    }

    const previousValue = this._previousValue || '';
    const selectionStart = inputElement.selectionStart || 0;
    const selectionEnd = inputElement.selectionEnd || 0;

    // Strip all non-numeric characters
    let numericValue = this.stripNonNumeric(currentValue);

    // Limit to 10 digits
    numericValue = numericValue.substring(0, 10);

    // Determine if user is deleting or typing
    const isDeleting = currentValue.length < previousValue.length;

    if (isDeleting) {
      numericValue = this.handleDeletion(numericValue, previousValue, selectionStart);
    }

    // Format the numeric value with phone mask
    const formattedValue = this.formatPhoneNumber(numericValue, isDeleting);

    // Calculate the new cursor position
    const newCursorPosition = this.calculateCursorPosition(
      currentValue,
      formattedValue,
      previousValue,
      selectionStart,
      selectionEnd,
      isDeleting
    );

    // Update the form control value without emitting event to prevent recursion
    this._phoneControl.setValue(formattedValue, { emitEvent: false });

    // Update previous value for next change
    this._previousValue = formattedValue;

    // Restore cursor position
    inputElement.setSelectionRange(newCursorPosition.start, newCursorPosition.end);
  }

  /**
   * Get the native input element
   */
  private getInputElement(): HTMLInputElement | null {
    const element = document.getElementById(this._elementId);
    if (!element || !(element instanceof HTMLInputElement)) {
      console.warn(`PhoneMaskDirective: Could not find input element with id "${this._elementId}"`);
      return null;
    }
    return element;
  }

  /**
   * Remove all non-numeric characters from a string
   */
  private stripNonNumeric(value: string): string {
    return (value || '').replace(/\D/g, '');
  }

  /**
   * Handle special logic when user is deleting characters
   * If deleting a closing parenthesis, also remove the last digit
   */
  private handleDeletion(numericValue: string, previousValue: string, cursorPosition: number): string {
    const lastChar = previousValue.charAt(previousValue.length - 1);
    // If cursor is at the end and last character was ')', remove the last digit
    if (previousValue.length < cursorPosition && lastChar === ')') {
      return numericValue.substring(0, numericValue.length - 1);
    }
    return numericValue;
  }

  /**
   * Format numeric string as US phone number: (XXX) XXX-XXXX
   * When deleting, uses slightly different pattern to improve UX
   */
  private formatPhoneNumber(numericValue: string, isDeleting: boolean): string {
    if (numericValue.length === 0) {
      return '';
    }

    if (numericValue.length <= 3) {
      // When deleting: (XXX  - keep opening paren only
      // When typing: (XXX) - include closing paren
      return isDeleting ? numericValue.replace(/^(\d{0,3})/, '($1') : numericValue.replace(/^(\d{0,3})/, '($1)');
    }

    if (numericValue.length <= 6) {
      // (XXX) XXX
      return numericValue.replace(/^(\d{0,3})(\d{0,3})/, '($1) $2');
    }

    // (XXX) XXX-XXXX
    return numericValue.replace(/^(\d{0,3})(\d{0,3})(.*)/, '($1) $2-$3');
  }

  /**
   * Calculate the new cursor position after formatting
   * Handles special characters and middle-of-string editing
   */
  private calculateCursorPosition(
    currentValue: string,
    formattedValue: string,
    previousValue: string,
    selectionStart: number,
    selectionEnd: number,
    isDeleting: boolean
  ): { start: number; end: number } {
    if (isDeleting) {
      // When deleting, maintain cursor position
      return { start: selectionStart, end: selectionEnd };
    }

    // Check if user is typing in the middle of the string
    if (previousValue.length >= selectionStart) {
      const charAtCursor = currentValue.charAt(selectionStart);
      let cursorOffset = 0;

      // Adjust cursor position based on special characters typed over
      if (charAtCursor === '(') {
        cursorOffset = 1;
      } else if (charAtCursor === ')') {
        cursorOffset = 2; // +2 includes the space after ')'
      } else if (charAtCursor === '-' || charAtCursor === ' ') {
        cursorOffset = 1;
      }

      return {
        start: selectionStart + cursorOffset,
        end: selectionEnd + cursorOffset
      };
    }

    // User is typing at the end - advance cursor by 2 positions for standard typing flow
    return {
      start: selectionStart + 2,
      end: selectionEnd + 2
    };
  }
}
